import { ReactNode } from 'react';

import { Box, Theme, Typography, styled } from '@mui/material';

import { isDarkMode } from '@localstack/services';
import { AllowedPlanChipPlans } from '@localstack/types';
import { PlanChip, SidebarIcon } from '@localstack/ui';

import { CustomerLayout, CustomerLayoutProps } from './CustomerLayout';

type BasicPageLayoutVariants = 'default' | 'compact' | 'full';

const getLayoutVariants = (theme: Theme): Record<BasicPageLayoutVariants, React.CSSProperties> => ({
  default: {
    maxWidth: '1280px',
    minWidth: '700px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '0',
    },
  },
  compact: {
    width: '493px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  full: {
    width: '100%',
    maxWidth: 'none',
    minWidth: '700px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '0',
    },
  },
});

interface PageContainerProps {
  variant: BasicPageLayoutVariants;
}

const PageContainer = styled('div')<PageContainerProps>(({ theme, variant }) => ({
  ...getLayoutVariants(theme)[variant],
  margin: '56px 0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
}));

/**
 * Horizontal padding used to align content with the page title/subtitle on the `full` variant,
 * where the outer container itself carries no gutter. Wrap any section (e.g. a toolbar) that
 * should be inset from the page edges instead of running edge-to-edge like a full-width grid.
 */
export const PageGutter = styled('div')<{ variant?: BasicPageLayoutVariants }>(({ theme, variant }) => ({
  ...(variant === 'full' && {
    padding: theme.spacing(0, 7),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2),
    },
  }),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: isDarkMode(theme) ? theme.palette.grey[200] : theme.palette.grey[800],
  paddingTop: theme.spacing(0.85),
  paddingBottom: theme.spacing(0.85),
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: isDarkMode(theme) ? theme.palette.grey[400] : theme.palette.grey[700],
}));

/**
 * Interface for BasicPageLayout props
 */
interface BasicPageLayoutProps {
  /** The main title of the page */
  title: string | ReactNode;
  /** The subtitle text displayed below the title */
  subtitle?: ReactNode;
  /** Optional plan-availability chip rendered next to the title (e.g. "PREVIEW") */
  planFamily?: AllowedPlanChipPlans;
  /**
   * The variant of the container. Width of the container is changed based on the variant. Default: `default`
   * - `default`: capped at 1280px, for standard content pages
   * - `compact`: capped at 493px, for narrow forms
   * - `full`: spans the full available width, for wide tables/grids
   */
  variant?: BasicPageLayoutVariants;
  /** Child components to be rendered below the title and subtitle */
  children?: ReactNode;
}

/**
 * Simple page component containing title and subtitle with properly typed props
 */
export const BasicPageLayout = ({
  title,
  subtitle,
  planFamily,
  children,
  variant = 'default',
  ...rest
}: BasicPageLayoutProps & CustomerLayoutProps): JSX.Element => {
  return (
    <CustomerLayout title={title} {...rest}>
      <PageContainer variant={variant}>
        <PageGutter variant={variant}>
          {title && (
            <Box display="flex" alignItems="center" gap={1}>
              <SidebarIcon />
              <StyledTitle variant="h2">{title}</StyledTitle>
              {planFamily && <PlanChip planFamily={planFamily} withStyles={false} />}
            </Box>
          )}
          {subtitle && <StyledSubtitle variant="body1">{subtitle}</StyledSubtitle>}
        </PageGutter>
        {children}
      </PageContainer>
    </CustomerLayout>
  );
};

export default BasicPageLayout;
